/*
 * Gift box upsell inside the cart drawer.
 *
 * Adds the chosen gift product to the cart with the Cart AJAX API and re-renders the
 * drawer through the Section Rendering API, exactly like product-form.js does. Because
 * the add goes through /cart/add.js with a variant id, Shopify Bundles keep working:
 * the bundle parent is added and its component lines come back in the re-rendered drawer.
 */
if (!customElements.get('cart-gift-upsell')) {
  // Guards against a second cleanup being fired while one is still on its way out, and against
  // looping if a removal never takes (each successful pass clears one gift line).
  const MAX_CLEANUP_ATTEMPTS = 5;
  let orphanCleanupInFlight = false;
  let orphanCleanupAttempts = 0;

  customElements.define(
    'cart-gift-upsell',
    class CartGiftUpsell extends HTMLElement {
      constructor() {
        super();

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.addEventListener('click', this.onClickHandler.bind(this));
        // Any change event raised in here stays in here, so it can never reach a cart
        // line-item listener further up the drawer.
        this.addEventListener('change', (event) => {
          event.stopPropagation();
          if (event.target.closest('[data-gift-select]')) this.updatePreview();
        });
      }

      connectedCallback() {
        this.removeOrphanGifts();
        this.updatePreview();
      }

      // Shows the picture and price of whichever gift box is selected, from the data on the option.
      updatePreview() {
        const select = this.querySelector('[data-gift-select]');
        if (!select) return;

        const option = select.options[select.selectedIndex];
        if (!option) return;

        const image = this.querySelector('[data-gift-image]');
        if (image) {
          const source = option.dataset.image;
          image.toggleAttribute('hidden', !source);
          if (source) {
            image.src = source;
            image.alt = option.dataset.imageAlt || '';
          }
        }

        const price = this.querySelector('[data-gift-price]');
        if (price) price.textContent = option.dataset.price || '';
      }

      /*
       * Gift products are an add-on, never a cart on their own. Liquid marks the gift lines
       * as orphaned whenever nothing else is left in the cart, and they get removed here.
       * Bundle component lines are excluded on the Liquid side, so removing a gift bundle's
       * parent line takes its components with it.
       */
      removeOrphanGifts() {
        const lines = (this.dataset.orphanGiftLines || '').split(',').filter(Boolean);

        if (!lines.length) {
          // Nothing left to clear. The cart page renders its items outside the drawer, so it
          // needs one reload to catch up with what was removed through the drawer.
          const cleanedUpOnCartPage = orphanCleanupAttempts > 0 && document.getElementById('main-cart-items');
          orphanCleanupAttempts = 0;
          if (cleanedUpOnCartPage) window.location.reload();
          return;
        }

        if (orphanCleanupInFlight || orphanCleanupAttempts >= MAX_CLEANUP_ATTEMPTS) return;

        orphanCleanupInFlight = true;
        orphanCleanupAttempts += 1;

        /*
         * Deferred out of the callback on purpose. This element is connected in the middle of
         * whatever cart operation just re-rendered the drawer, so acting here would work
         * against a cart that operation has not finished with yet.
         */
        setTimeout(() => {
          orphanCleanupInFlight = false;

          // The theme is still mid-update; the render it is about to do will call us back.
          if (document.querySelector('.cart__items--disabled')) return;

          /*
           * Clicks the theme's own remove button for the line, so removal runs through
           * exactly the same path as a shopper clicking the bin: one cart request, one
           * render, done by CartItems.updateQuantity. One line per pass — the render it
           * triggers brings us back here for the next one.
           */
          const drawer = document.getElementById('CartDrawer');
          drawer?.querySelector(`cart-remove-button[data-index="${lines[0]}"] button`)?.click();
        });
      }

      onClickHandler(event) {
        const button = event.target.closest('[data-gift-add]');
        if (!button) return;

        event.preventDefault();
        this.addToCart(button);
      }

      addToCart(button) {
        if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

        const item = button.closest('[data-gift-item]');
        // The gift boxes share one dropdown, so the chosen option carries the variant id.
        const select = item?.querySelector('[data-gift-select]');
        const variantId = parseInt(select?.value || button.dataset.variantId, 10);
        if (!item || !variantId) return;

        const quantity = 1;

        this.setError(item);
        this.toggleLoading(button, true);

        const body = {
          items: [{ id: variantId, quantity }],
          sections_url: window.location.pathname,
        };

        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          this.cart.setActiveElement(document.activeElement);
        }

        const config = fetchConfig('javascript');
        config.body = JSON.stringify(body);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.setError(item, response.description || response.message);
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'cart-gift-upsell',
                productVariantId: String(variantId),
                errors: response.errors || response.description,
                message: response.message,
              });
              return;
            }

            if (!this.cart) {
              window.location = routes.cart_url;
              return;
            }

            this.cart.classList.remove('is-empty');

            /*
             * The item is in the cart from here on, so nothing below may be reported as a
             * failed add. A subscriber that throws would otherwise land in the catch below
             * and show "we could not update your cart" over a cart that updated fine.
             */
            try {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'cart-gift-upsell',
                productVariantId: String(variantId),
                cartData: response,
              });
            } catch (error) {
              console.error('[cart-gift-upsell] a cartUpdate subscriber threw', error);
            }

            try {
              // The theme's own drawer refresh, same call product-form.js makes.
              this.cart.renderContents(response);
            } catch (error) {
              console.error('[cart-gift-upsell] drawer repaint failed', error);
              this.cart.open?.();
            }
          })
          .catch((error) => {
            console.error(error);
            this.setError(item, window.cartStrings?.error);
          })
          .finally(() => {
            this.toggleLoading(button, false);
          });
      }

      toggleLoading(button, isLoading) {
        if (!button.isConnected) return;

        button.classList.toggle('loading', isLoading);
        if (isLoading) {
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('aria-disabled');
        }
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      }

      setError(item, message) {
        const errorElement = item.querySelector('[data-gift-error]');
        if (!errorElement) return;

        errorElement.textContent = message || '';
        errorElement.toggleAttribute('hidden', !message);
      }
    }
  );
}
